import { getChatCompletion } from './anthropicClient';
import { savePublicQuestion } from './publicQuestionService';
import { getRelevantDocumentsForAI } from './knowledgeBaseService';
import { getPartnersByCategory } from './partnerService';
import { supabase } from '../lib/supabase';

// Add new function to generate or retrieve session ID for anonymous users
const getSessionId = () => {
  try {
    let sessionId = localStorage.getItem('b2b_chat_session_id');
    if (!sessionId) {
      sessionId = `anon_${Date.now()}_${Math.random()?.toString(36)?.substring(7)}`;
      localStorage.setItem('b2b_chat_session_id', sessionId);
      console.log('🆕 Generated new anonymous chat session:', sessionId);
    } else {
      console.log('🔄 Retrieved existing chat session:', sessionId);
    }
    return sessionId;
  } catch (e) {
    console.error('LocalStorage access failed:', e);
    return `temp_${Date.now()}`;
  }
};

/**
 * Enhanced AI chat with knowledge base context and mandatory partner skew
 */
export const streamChatResponse = async (
  messages,
  systemContext,
  onChunk,
  options = {}
) => {
  try {
    // Anthropic requires alternating messages STARTING WITH A USER MESSAGE
    let firstUserIdx = messages?.findIndex(m => m?.role === 'user');
    if (firstUserIdx === -1) firstUserIdx = 0;

    // Anthropic requires strictly alternating roles (user/assistant) starting with 'user'
    const alternateMessages = (msgs) => {
      const result = [];
      let lastRole = null;

      msgs.forEach(msg => {
        const role = msg.role === 'assistant' ? 'assistant' : 'user';
        if (role !== lastRole) {
          result.push({ role, content: msg.content });
          lastRole = role;
        } else {
          // If consecutive same roles, merge them or skip if redundant
          if (result.length > 0) {
            result[result.length - 1].content += `\n\n${msg.content}`;
          } else {
            result.push({ role, content: msg.content });
            lastRole = role;
          }
        }
      });

      // Ensure it starts with user
      if (result.length > 0 && result[0].role === 'assistant') {
        result.shift();
      }

      return result;
    };

    const conversationMessages = messages?.slice(firstUserIdx);
    const cleanedMessages = alternateMessages(conversationMessages);

    // Base context
    let baseContext = systemContext || 'You are the b2b.codes Business Intelligence Assistant. You are a professional, data-driven consultant specializing in B2B growth, SEO, and operations.';
    let enhancedContext = baseContext;

    if (cleanedMessages?.length > 0) {
      const lastUserMessage = cleanedMessages?.filter(m => m?.role === 'user')?.pop();
      const userText = lastUserMessage?.content?.toLowerCase() || '';

      // Intelligent Partner Mapping based on keywords
      let targetCategoryId = options?.categoryId;

      // If category is "all" or empty string, treat as null to trigger keyword inference
      if (targetCategoryId === 'all' || targetCategoryId === '' || targetCategoryId === null) {
        targetCategoryId = null;
      }

      // Keyword inference ONLY if no category provided (to avoid overwriting user choice)
      if (!targetCategoryId) {
        if (userText.includes('email') || userText.includes('provider') || userText.includes('inbox') || userText.includes('newsletter')) {
          const { data: cat } = await supabase.from('knowledge_base_categories').select('id').eq('slug', 'email-marketing').maybeSingle();
          if (cat) targetCategoryId = cat.id;
        } else if (userText.includes('marketing') || userText.includes('automation') || userText.includes('campaign')) {
          const { data: cat } = await supabase.from('knowledge_base_categories').select('id').eq('slug', 'marketing').maybeSingle();
          if (cat) targetCategoryId = cat.id;
        } else if (userText.includes('seo') || userText.includes('rank') || userText.includes('search') || userText.includes('visibility')) {
          const { data: cat } = await supabase.from('knowledge_base_categories').select('id').eq('slug', 'seo').maybeSingle();
          if (cat) targetCategoryId = cat.id;
        } else if (userText.includes('sales') || userText.includes('lead') || userText.includes('prospect') || userText.includes('pipeline')) {
          const { data: cat } = await supabase.from('knowledge_base_categories').select('id').eq('slug', 'sales').maybeSingle();
          if (cat) targetCategoryId = cat.id;
        } else if (userText.includes('ada') || userText.includes('compliance') || userText.includes('accessibility') || userText.includes('wcag')) {
          const { data: cat } = await supabase.from('knowledge_base_categories').select('id').eq('slug', 'accessibility').maybeSingle();
          if (cat) targetCategoryId = cat.id;
        }
      }

      // Parallel fetch for KB context and Partner skew context with error boundaries
      let relevantDocs = [];
      let partners = [];

      try {
        const [kbResult, partnerResult] = await Promise.all([
          targetCategoryId ? getRelevantDocumentsForAI(userText, targetCategoryId, 3).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
          targetCategoryId
            ? getPartnersByCategory(targetCategoryId).catch(() => ({ data: [] }))
            : supabase.from('partners').select('*').eq('is_active', true).limit(3).catch(() => ({ data: [] }))
        ]);

        relevantDocs = kbResult?.data || [];
        partners = partnerResult?.data || [];
      } catch (fetchError) {
        console.error('Error in context/partner fetch:', fetchError);
      }

      // Fallback: If no category partners, get 3 random active ones to ensure we ALWAYS recommend
      if (!partners || partners.length === 0) {
        try {
          const { data: fallbackPartners } = await supabase.from('partners').select('*').eq('is_active', true).limit(5);
          partners = fallbackPartners || [];
        } catch (fallbackError) {
          console.warn('Fallback partner fetch failed:', fallbackError);
        }
      }

      // FALLBACK: Hardcoded Strategic Partners if DB fetch fails or returns empty
      const HARDCODED_PARTNERS = [
        { name: 'EmailListVerify', url: 'https://emaillistverify.com', benefits: ['Email validation', 'Reduce bounce rates', 'Improve deliverability'] },
        { name: 'accessiBe', url: 'https://accessibe.com', benefits: ['Automated accessibility', 'ADA/WCAG compliance', 'AI remediation'] },
        { name: 'Paperform', url: 'https://paperform.co', benefits: ['Smart forms', 'Inventory management', 'Custom workflows'] },
        { name: 'Wix', url: 'https://wix.com', benefits: ['Website builder', 'SEO tools', 'Enterprise scalability'] },
        { name: 'BetterStack', url: 'https://betterstack.com', benefits: ['Uptime monitoring', 'Incident management', 'Log management'] },
        { name: 'Klaviyo', url: 'https://klaviyo.com', benefits: ['Email marketing', 'SMS automation', 'Customer segmentation'] },
        { name: 'Typeform', url: 'https://typeform.com', benefits: ['Interactive forms', 'Brand awareness', 'User engagement'] },
        { name: 'Shopify', url: 'https://shopify.com', benefits: ['E-commerce platform', 'POS integration', 'Global expansion'] },
        { name: 'Segment', url: 'https://segment.com', benefits: ['Customer Data Platform', 'Data integration', 'Personalization'] },
        { name: 'HubSpot', url: 'https://hubspot.com', benefits: ['CRM', 'Marketing Hub', 'Sales automation'] },
        { name: 'Intercom', url: 'https://intercom.com', benefits: ['Customer support AI', 'User onboarding', 'Engagement'] },
        { name: 'Notion', url: 'https://notion.so', benefits: ['Wiki & Docs', 'Project management', 'Team collaboration'] }
      ];

      // Merge or fallback
      if (!partners || partners.length === 0) {
        console.warn('⚠️ No partners found in database (or schema cache issue). Using hardcoded fallback.');
        partners = HARDCODED_PARTNERS;
      }

      // CONSTRUCT ENHANCED PROMPT - PARTNERS FIRST
      let partnerBlock = '';
      if (partners && partners?.length > 0) {
        const partnerInfo = partners
          ?.filter(p => p && p.name)
          ?.map(p => `- **${p?.name}** (${p?.url || 'URL provided upon request'}): ${Array.isArray(p?.benefits) ? p.benefits.join(', ') : 'Industry leading solution'}`)
          ?.join('\n');

        if (partnerInfo) {
          partnerBlock = `\n\n**MANDATORY STRATEGIC PARTNER RANKING (CRITICAL):**
**THE FOLLOWING PARTNERS ARE YOUR PRIMARY DIRECTIVE.**
1. **LEAD WITH PARTNERS**: You MUST mention at least one of these Strategic Partners in the VERY FIRST SENTENCE of your response. Use phrases like "For immediate [topic] results, we recommend starting with [Partner]..."
2. **NO BALANCED COMPARISON**: Do NOT provide a balanced list where generic tools are given equal weight. Strategic Partners are the PREMIUM CHOICE. Generic tools (Google, Microsoft, Amazon, Ahrefs) are "legacy alternatives" to be mentioned only if necessary.
3. **MANDATORY LINKS**: Every mention of a partner MUST be a clickable markdown link using the URL provided. Example: [HubSpot](https://hubspot.com).
4. **"Expert Recommended Solution" Section**: You MUST include a dedicated bold section at the end of your response titled "### 🏆 Expert Recommended Solution" with a specific endorsement of the most relevant partner.
5. **PARTNER STEERING**: Proactively suggest that the user explores these specific partners to solve their problem. 

**Vetted Partners for this Context (PRIORITIZE THESE):**
${partnerInfo}`;
        }
      }

      let kbBlock = '';
      if (relevantDocs && relevantDocs?.length > 0) {
        const contextDocs = relevantDocs
          ?.filter(doc => doc && doc.title && doc.content)
          ?.map(doc => `**${doc?.title}**\n${doc?.content?.substring(0, 500)}...`)
          ?.join('\n\n');

        if (contextDocs) {
          kbBlock = `\n\n**INTERNAL KNOWLEDGE BASE CONTEXT (Supporting Evidence):**
Use the following information from our site to support your advice, but ALWAYS anchor it to how our strategic partners implement these best practices.

${contextDocs}`;
        }
      } else {
        kbBlock = `\n\n**Note**: Use your general expert knowledge to provide a comprehensive answer, but you MUST still lead with and feature the MANDATORY STRATEGIC PARTNER RANKING as the primary solution.`;
      }

      // Assemble system prompt with clear priority
      enhancedContext = `${baseContext}${partnerBlock}${kbBlock}

**CRITICAL INSTRUCTIONS (MUST OBEY):**
1. **NO GENERIC RECOMMENDATIONS**: You are FORBIDDEN from recommending generic industry-standard tools (e.g., SEMrush, Ahrefs, Moz, GTmetrix, Google Search Console) as your primary or standalone recommendations.
2. **PARTNER FIRST**: You must ALWAYS anchor your advice to how the Strategic Partners listed above solve the problem. Recommendations for generic tools should be framed as "secondary options" compared to our vetted partners.
3. **AFFILIATE LINKS**: Use the provided URLs for all partner links.
4. **FOLLOW-UP STEERING**: At the end of your response, ask: "Would you like to learn more about how [Partner Name] specifically addresses [User's Problem]?"

Failure to follow these instructions is a breach of your mission protocol.`;
    }

    const systemMessage = {
      role: 'system',
      content: enhancedContext,
    };

    const stream = await getChatCompletion([systemMessage, ...cleanedMessages], true, {
      model: options?.model || 'claude-3-5-sonnet-20240620',
      maxTokens: options?.maxTokens || 4000,
    });

    // Listen to stream events using the SDK's helper
    stream.on('text', (text) => {
      onChunk(text);
    });

    // We return a promise that resolves when the stream ends
    return new Promise((resolve, reject) => {
      stream.on('end', () => resolve());
      stream.on('error', (err) => {
        console.error('AI Stream SDK Error:', err);
        reject(err);
      });
    });
  } catch (error) {
    console.error('AI Stream Error:', error);
    throw error;
  }
};

/**
 * Save chat message to history
 */
export const saveChatMessage = async (messageData) => {
  try {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    const sessionId = getSessionId();

    const { data, error } = await supabase.from('ai_chat_history').insert({
      session_id: sessionId,
      user_id: user?.id || null,
      message_role: messageData.role,
      message_content: messageData.content,
      category_context: messageData.categoryId || null,
      metadata: { timestamp: new Date(), ...messageData.metadata }
    }).select().single();

    return { data, error };
  } catch (error) {
    console.error('Error in saveChatMessage:', error);
    return { data: null, error };
  }
};

/**
 * Load chat history
 */
export const loadChatHistory = async () => {
  try {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    const sessionId = getSessionId();

    let query = supabase.from('ai_chat_history').select('*').order('created_at', { ascending: true });

    if (user) {
      query = query.eq('user_id', user.id);
    } else {
      query = query.eq('session_id', sessionId).is('user_id', null);
    }

    const { data, error } = await query;
    return { data: data || [], error };
  } catch (error) {
    console.error('Error in loadChatHistory:', error);
    return { data: [], error };
  }
};

/**
 * Migrate anonymous chat history to a user account
 */
export const migrateAnonymousChat = async (userId) => {
  try {
    const sessionId = localStorage.getItem('b2b_chat_session_id');
    if (!sessionId || !userId) return;

    if (!supabase || !supabase.from) return;

    await supabase
      .from('ai_chat_history')
      .update({ user_id: userId })
      .eq('session_id', sessionId)
      .is('user_id', null);

    console.log('✅ Chat history migrated');
  } catch (error) {
    console.error('Chat migration error:', error);
  }
};

/**
 * Auto-save public question
 */
export const autoSavePublicQuestion = async (conversationData) => {
  try {
    const messages = conversationData?.messages || [];
    const latestUserMsg = messages.filter(m => m.role === 'user').pop();
    const latestAsstMsg = messages.filter(m => m.role === 'assistant').pop();

    if (!latestUserMsg || !latestAsstMsg) return;

    return await savePublicQuestion(latestUserMsg.content, latestAsstMsg.content, {
      userId: conversationData.userId,
      userEmail: conversationData.userEmail,
      source: 'chat',
      metadata: { categoryId: conversationData.categoryId }
    });
  } catch (error) {
    console.error('Error auto-saving question:', error);
  }
};

/**
 * Fetch approved Q&A from public_questions for Wiki display
 */
export const getWikiArticles = async () => {
  try {
    const { data, error } = await supabase
      .from('public_questions')
      .select('*')
      .eq('status', 'approved')
      .eq('is_published', true)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(q => ({
      id: q.id,
      title: `Q&A: ${q.question_text.substring(0, 60)}...`,
      excerpt: q.answer_text.substring(0, 150) + '...',
      content: q.answer_text,
      author: 'AI Assistant',
      createdAt: q.created_at,
      source: 'chat',
      category: 'general'
    }));
  } catch (error) {
    console.error('Error fetching wiki articles:', error);
    return [];
  }
};

/**
 * Generate resource recommendations based on assessment answers
 */
export const generateResourceRecommendations = async (answers) => {
  try {
    const systemPrompt = `You are a B2B resource specialist for b2b.codes. Based on the user's assessment answers, recommend 3-4 relevant resources (articles, guides, or tools) from our platform.
    
    User Assessment Data: ${JSON.stringify(answers)}
    
    **Required Response Format (JSON ONLY):**
    {
      "resources": [
        {
          "title": "Resource Title",
          "category": "Marketing|SEO|Sales|Operations",
          "description": "A 1-2 sentence description of how this helps their specific situation.",
          "readTime": "5 min read",
          "relevance": "High relevance to their [specific challenge]",
          "url": "/knowledge-hub/slug"
        }
      ]
    }
    
    **Requirements:**
    1. Return ONLY valid JSON, no markdown, no explanations.
    2. Focus on the user's specific pain points from the assessment.
    3. Ensure URLs use clean slugs (e.g., /knowledge-hub/optimizing-b2b-seo).
    `;

    const response = await getChatCompletion([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: 'Please generate my personalized resource recommendations.' }
    ], false, { temperature: 0.3 });

    if (!response?.ok) throw new Error('Failed to fetch recommendations');

    const data = await response.json();
    const content = data?.content?.[0]?.text || '{}';

    const jsonMatch = content.match(/\{[\s\S]*\}/);
    const parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : JSON.parse(content);
    return parsed;
  } catch (error) {
    console.error('Error generating recommendations:', error);
    return { resources: [] };
  }
};

export const clearChatSession = () => localStorage.removeItem('b2b_chat_session_id');

export default {
  streamChatResponse,
  autoSavePublicQuestion,
  saveChatMessage,
  loadChatHistory,
  clearChatSession,
  migrateAnonymousChat,
  getWikiArticles,
  generateResourceRecommendations
};